import React, { useState, useCallback, useEffect, useRef } from "react";
import * as AuthHelpers from "../common/helpers/auth";
import { withRouter, useHistory } from "react-router-dom";
import jwtDecode from "jwt-decode";
import * as Api from "../api";
import Cookies from "js-cookie";
import { currentUserQuery } from "../api/userQueries";
import { useApi as useApiRedux } from "../api/useApiRedux";
import { usersInEnabledWorkGroups } from "../api/userQueries";
import { useApi } from "../api/useApi";
import {
  getCachedAuthConfig,
  fetchAuthConfig,
  isAcesWebProvider,
} from "../services/authConfigService";
export const AuthContext = React.createContext();

const AuthProvider = (props) => {
  const [{ errors, data }, doFetch] = useApiRedux();

  //Init Data Fetch
  const [
    { loading: groupDataLoading, data: groupData, errors: checkGroupErrors },
    doCheckUserGroup,
  ] = useApi();

  const userObject = AuthHelpers.buildUser();
  const [user, setUser] = useState(userObject ? userObject : null);
  const [userPermissioned, setUserPermissioned] = useState(false);
  const [loginErrors, setLoginErrors] = useState(null);
  const [authConfig, setAuthConfig] = useState(() => getCachedAuthConfig());
  const [acesLoginPending, setAcesLoginPending] = useState(false);
  const acesAbortControllerRef = useRef(null);
  let history = useHistory();

  // Fetch auth config on mount (stale-while-revalidate)
  useEffect(() => {
    fetchAuthConfig().then((config) => {
      setAuthConfig((prev) => {
        // Only update if changed to avoid unnecessary re-renders
        if (JSON.stringify(prev) !== JSON.stringify(config)) {
          return config;
        }
        return prev;
      });
    });
  }, []);
  // Shared post-login flow: verify user via GraphQL, handle redirect, set sessionPhoto
  const completeLogin = useCallback(
    async (token, signal) => {
      Cookies.set("token", token);

      const res = await Api.request(currentUserQuery, {}, signal);

      if (res?.data?.currentUser) {
        let redirectUrl;
        const localRedirectUrl = window.localStorage.getItem("redirect-url");
        if (localRedirectUrl && localRedirectUrl !== "/") {
          redirectUrl = localRedirectUrl;
        } else {
          redirectUrl = "/welcome";
        }

        // Clear the redirect URL from localStorage before redirecting
        window.localStorage.removeItem("redirect-url");

        if (res?.data?.currentUser?.profilePhoto) {
          window.localStorage.setItem(
            "sessionPhoto",
            res.data.currentUser.profilePhoto
          );
        }

        setUser(AuthHelpers.buildUser());
        history.push(redirectUrl);
        return true;
      }
      return false;
    },
    [history]
  );

  useEffect(() => {
    if (data) {
      const abortController = new AbortController();

      completeLogin(data, abortController.signal).catch((err) => {
        if (!abortController.signal.aborted)
          setLoginErrors([{ message: err.message }]);
      });

      return () => abortController.abort();
    }
  }, [data, completeLogin]);

  useEffect(() => {
    if (errors) {
      setUser(null);
      setLoginErrors([{ message: "Login Error, Please Try Again Later." }]);
    }
  }, [errors]);

  const login = useCallback(() => {
    // Store redirect URL for post-login navigation
    const fullRedirectUrl = `${props.location.pathname}${props.location.search}`;
    window.localStorage.setItem("redirect-url", fullRedirectUrl);

    if (isAcesWebProvider(authConfig)) {
      // AcesWeb: Redirect to external login URL
      if (authConfig.loginUrl) {
        window.location.href = authConfig.loginUrl;
      } else {
        console.error("AcesWeb login URL not configured");
      }
    } else {
      // Azure AD: Use existing OAuth2 flow
      const stateObject = JSON.stringify({
        origin: window.location.origin,
        redirect: `${window.location.origin}/login_redirect`,
      }).replace(/\\n/g, "");

      const nonce = Math.floor(1000000000 + Math.random() * 900000);
      const loginURL = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?response_type=id_token&scope=User.Read.All%20Group.Read.All%20openid%20profile&client_id=${
        process.env.REACT_APP_APPCLIENTID
      }&redirect_uri=${process.env.REACT_APP_LOGINURL}&state=${encodeURIComponent(
        stateObject
      )}&nonce=${nonce}&prompt=select_account&response_mode=form_post`;

      window.location.href = loginURL;
    }
  }, [props.location.pathname, props.location.search, authConfig]);

  const logout = useCallback(() => {
    // Clear local state for all providers
    Cookies.remove("token");
    localStorage.removeItem("sessionPhoto");
    localStorage.removeItem("redirect-url");
    sessionStorage.clear();
    setUser(null);

    if (isAcesWebProvider(authConfig)) {
      // AcesWeb: Just clear local state, redirect to home
      window.location.href = authConfig.logoutUrl;
    } else {
      // Azure AD: Full logout including Azure AD session
      const logoutURL = `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(
        window.location.protocol + "//" + window.location.hostname
      )}`;
      window.location.href = logoutURL;
    }
  }, [authConfig]);

  const doLogin = useCallback(
    (authToken) => {
      const adToken = jwtDecode(authToken);

      const variables = { email: adToken.preferred_username, authToken };

      doFetch({ query: "/authentication", body: variables });
    },
    [doFetch]
  );

  const doAcesLogin = useCallback(
    async (authCode, dbCode) => {
      // Abort any in-flight ACES login
      if (acesAbortControllerRef.current) {
        acesAbortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      acesAbortControllerRef.current = abortController;

      setAcesLoginPending(true);
      setLoginErrors(null);

      try {
        const response = await fetch("/api/v1/aces-authentication", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ authCode, dbCode }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          setLoginErrors([
            { message: "Authentication failed. Please try again." },
          ]);
          setAcesLoginPending(false);
          return;
        }

        const data = await response.json();
        const token = data.token;

        if (!token) {
          setLoginErrors([{ message: "No token received from server." }]);
          setAcesLoginPending(false);
          return;
        }

        const success = await completeLogin(token, abortController.signal);
        if (!success) {
          setLoginErrors([{ message: "Login Error, Please Try Again Later." }]);
        }
        setAcesLoginPending(false);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        if (process.env.NODE_ENV !== "production") {
          console.error("Auth code exchange error:", error);
        }
        setLoginErrors([{ message: "Login Error, Please Try Again Later." }]);
        setAcesLoginPending(false);
      }
    },
    [completeLogin]
  );

  // Cleanup ACES abort controller on unmount
  useEffect(() => {
    return () => {
      if (acesAbortControllerRef.current) {
        acesAbortControllerRef.current.abort();
      }
    };
  }, []);

  const CheckGroup = useCallback(
    (userId) => {
      doCheckUserGroup({
        query: usersInEnabledWorkGroups,
        variables: {
          where: {
            id: { eq: Number(userId) },
            enabled: { eq: true },
          },
        },
      });
    },
    [doCheckUserGroup]
  );

  useEffect(() => {
    if (user?.id) {
      CheckGroup(user.id);
    }
  }, [CheckGroup, user]);

  useEffect(() => {
    if (groupData) {
      const workgroupTotal = groupData?.usersInEnabledWorkGroups?.totalCount;
      const isFoundInWorkgroup = workgroupTotal >= 1;
      setUserPermissioned(isFoundInWorkgroup);
    }
  }, [groupData, setUserPermissioned]);

  const value = React.useMemo(() => {
    return {
      user,
      loginErrors,
      login,
      logout,
      doLogin,
      doAcesLogin,
      acesLoginPending,
      userPermissioned,
      groupDataLoading,
      checkGroupErrors,
      authConfig,
    };
  }, [
    user,
    doLogin,
    doAcesLogin,
    acesLoginPending,
    loginErrors,
    login,
    logout,
    userPermissioned,
    groupDataLoading,
    checkGroupErrors,
    authConfig,
  ]);

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  );
};

const AuthProviderWithRouter = withRouter(AuthProvider);
export default AuthProviderWithRouter;
